import React, { useState, useEffect, useRef } from "react";
import ColumnChart from "../charts/ColumnChart";
import DoughNutChart from "../charts/DoughnutChart";
import PieChart from "../charts/PieChart";
import GroupColumnChart from "../charts/GroupColumnChart";
import Legend from "../charts/Legend";
import { util } from "../../utils/functions/custom";

import moment from "moment";
import dayjs from "dayjs";
import { Card, Col, Row, Tooltip, DatePicker, Empty, Tabs,Select } from "antd";

import "antd/dist/reset.css";

import { InfoCircleTwoTone } from "@ant-design/icons";

import ReportAPI from "../../services/nysAnalyticsService";
import {ReportTemplate} from "../custom/ReportTemplate";
import { reportTypeEnum } from "../custom/AppEnums";
import { ReportButtons } from "../custom/ReportButtons";
import { recordError } from "../../utils/logService";
import ProvinceEventsMap from "../charts/ProvinceEventsMap";
import { Option } from "antd/es/mentions";
import StackColumnChart from "../charts/StackColumnChart";
import { DownloadDataAsExcel } from "../custom";


export const NYSReport = () => {

  const [initialised, setInitialised] = useState(false);
  const [ageData, setAgeData] = useState([]);
  const [raceData, setRaceData] = useState([]);
  const [qualificationData, setQualificationData] = useState([]);
  const [participationData, setParticipationData] = useState([]);
  const [genderData, setGenderData] = useState([]);
  const [disabilityData, setDisabilityData] = useState([]);
  const [placementLocation, setPlacementLocation] = useState([]);
  const [candidateOrigin, setCandidateOrigin] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [candidateActivities, setCandidateActivities] = useState([]);
  const [placementDuration, setPlacementDuration] = useState([]);
  const [show, setShow] = useState(false);
  const [isGeo, setIsGeo] = useState(false);
  const [reportOutProps, setReportOutProps] = useState({
    data: [],
    color: "",
    type: "",
    year: 0,
    description: "",
    xLabel: "",
    yLabel: "",
    xField: "",
    yField: "",
    colorField: "",
  });

  const [reportYear, setReportYear] = useState(util.getCurrentRepotYear());
  const [financialYears, setFinancialYears] = useState(util.getCurrentFinancialYear());
  // const [currentDate, setcurrentDate] = useState(dayjs(Date()));
  // const initialDate = util.getInitialDate();

  // const monthFormat = 'YYYY'

  useEffect(() => {
    if (!initialised) {
      setFinancialYears(util.getCurrentFinancialYear());
      util.getYearsWithReports(ReportAPI, setFinancialYears, setReportYear);
      setInitialised(true);
    }
    updateAnalytics(reportYear);
  }, [reportYear]);

  // Disable some dates on the picker based on start and end dates
  const disabledDate = (current) => {
    return (
      current &&
      (current.valueOf() >= moment(endDate + 1, "YYYY") ||
        current.valueOf() <= moment(startDate, "YYYY"))
    );
  };

  const getAgeReportData = async (year) => {
    try {
      const response = await ReportAPI.getAge(year);
      if (response) {
        const { data } = response;
        setAgeData(data);
      }
      else {
       setAgeData([])
      }
    } catch (error) {
      setAgeData([])
      recordError(error);
    }
  };


  const getRaceData = async (year) => {
    try {
      const response = await ReportAPI.getRace(year);
      if (response) {
        const { data } = response;
        setRaceData(data);
      }
      else {
      setRaceData([])
      }
    } catch (error) {
      setRaceData([])
      recordError(error);
    }
  };

  const getQualificationData = async (year) => {
    try {
      const response = await ReportAPI.getQualification(year);
      if (response) {
        const { data } = response;
        setQualificationData(data);
      } else {
      setQualificationData([])
      }
    } catch (error) {
      setQualificationData([])
      recordError(error);
    }
  };

  const getParticipation = async () => {
    try {
      const response = await ReportAPI.getParticipation();
      if (response) {
        const { data } = response;
        setParticipationData(data);
        const minDate = data.reduce((prev, curr) =>
          prev.year < curr.year ? prev : curr
        );
        setStartDate(minDate.year);
        const maxDate = data.reduce((prev, curr) =>
          prev.year > curr.year ? prev : curr
        );
        setEndDate(maxDate.year);
      } else {
      setParticipationData([])
      setStartDate()
      setEndDate()
      }
    } catch (error) {
      setParticipationData([])
      setStartDate()
      setEndDate()
      recordError(error);
    }
  };

  const getGenderData = async (year) => {
    try {
      const response = await ReportAPI.getGender(year);
      if (response) {
        const { data } = response;
        setGenderData(data);
      } else {
      setGenderData([])
      }
    } catch (error) {
      setGenderData([])
      recordError(error);
    }
  };

  const getDisabilityData = async (year) => {
    try {
      const response = await ReportAPI.getDisability(year);
      if (response) {
        const { data } = response;
        setDisabilityData(data);
      } else {
      setDisabilityData([])
      }
    } catch (error) {
      setDisabilityData([])
      recordError(error);
    }
  };

  const getCandidateOrigin = async (year) => {
    try {
      const response = await ReportAPI.getCandidateOrigin(year);
      if (response && response.status == 200) {
        const { data } = response;
        setCandidateOrigin(data);
      } else {
        // response.status == 204
        setCandidateOrigin([]);
      }
    } catch (error) {
      setCandidateOrigin([])
      recordError(error);
    }
  };

  const getPlacementLocation = async (year) => {
    try {
      const response = await ReportAPI.getPlacementLocation(year);
      if (response && response.status == 200) {
        const { data } = response;
        setPlacementLocation(data);
      } else {
        // response.status == 204
        setPlacementLocation([]);
      }
    } catch (error) {
      setPlacementLocation([])
      recordError(error);
    }
  };

  const getCandidateActivities = async (year) => {
    try {
      const response = await ReportAPI.getCandidateActivities(year);
      if (response && response.status == 200) {
        const { data } = response;
        const unknownPreNYS = {
          "type": "Unknown",
          "percent": 0.0,
          "category": "Pre NYS",
          "value": 0.0,
          "year": year
        }
        data.push(unknownPreNYS)
        setCandidateActivities(data);
      } else {
        // response.status == 204
        setCandidateActivities([]);
      }
    } catch (error) {
      setCandidateActivities([])
      recordError(error);
    }
  };

  const getPlacementDuration = async (year) => {
    try {
      const response = await ReportAPI.getPlacementDuration(year);
      if (response && response.status == 200) {
        const { data } = response;
        setPlacementDuration(data);
      } else {
        // response.status == 204
        setPlacementDuration([]);
      }
    } catch (error) {
      setPlacementDuration([])
      recordError(error);
    }
  };

  const onYearChange = (value) => {
    setReportYear(value);
    // setcurrentDate(dayjs(value))
    // updateAnalytics(value);
  };

  const updateAnalytics = (repYear) => {
    getAgeReportData(repYear);
    getRaceData(repYear);
    getQualificationData(repYear);
    getParticipation();
    getGenderData(repYear);
    getDisabilityData(repYear);
    getCandidateOrigin(repYear);
    getPlacementLocation(repYear);
    getCandidateActivities(repYear);
    getPlacementDuration(repYear);
  };

  const handlePrint = (
    color,
    data,
    type,
    desc,
    title,
    xLabel = "None",
    yLabel = "None",
    xField = "None",
    colorField = "None",
    yField = "None"
  ) => {
    const rptProps = { ...reportOutProps };
    rptProps.color = color;
    rptProps.data = data;
    rptProps.type = type;
    rptProps.year = reportYear ? reportYear : 0;
    rptProps.description = desc;
    rptProps.title = title;
    rptProps.xLabel = xLabel;
    rptProps.yLabel = yLabel;
    rptProps.xField = xField;
    rptProps.yField = yField;
    rptProps.seriesField = colorField;

    setReportOutProps(rptProps);
    setShow(true);
    setIsGeo(false);
  };

  const handleGeoPrint = (title, description, data, geoType, dataSource) => {
    const rptProps = { ...reportOutProps };
    rptProps.title = title;
    rptProps.description = description;
    rptProps.color = undefined;
    rptProps.data = data;
    rptProps.type = geoType;
    rptProps.year = reportYear ? reportYear : 0;
    rptProps.dataSource = dataSource;
    setReportOutProps(rptProps);
    setShow(true);
    setIsGeo(true);
  };

  const handleDataDownload = (data, filename, chartType) => {
    let csvContent;

    if (chartType === reportTypeEnum.GROUPCOL) {
      const groupedData = util.groupDataByType(data, reportYear);

      csvContent =
        "data:text/csv;charset=utf-8," +
        util.convertArrayDataToCSV(groupedData);
      DownloadDataAsExcel(groupedData, filename);
    } else {
      csvContent =
        "data:text/csv;charset=utf-8," + util.convertArrayDataToCSV(data);
      DownloadDataAsExcel(data, filename);
    }

    //!  No longer allowing csv file download according to new requirements from business
    // const encodedURI = encodeURI(csvContent);

    // //Allow the selected to be downloadable through an anchor popup
    // const link = document.createElement("a");
    // link.setAttribute("href", encodedURI);
    // link.setAttribute("download", filename);
    // document.body.appendChild(link);
    // link.click();
    // document.body.removeChild(link);
  };

  const handlePrintCancel = () => {
    setShow(false);
    setIsGeo(false);
    const rptProps = { ...reportOutProps };
    rptProps.title = "";
    rptProps.description = "";
    rptProps.color = undefined;
    rptProps.data = [];
    rptProps.type = undefined;
    rptProps.year = 0;
    rptProps.dataSource = "";
    setReportOutProps(rptProps);
  };
  
  function Tab1() {
    return (
      <div className="report-content">
        <Row style={{ paddingBottom: 10 }} gutter={16}>
          <Col xs={24} md={8}>
            <Card
              title="Age Group"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of NYS candidates, based on age.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                ageData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "ageDougnut",
                        ageData,
                        reportTypeEnum.DOUGHNUT,
                        "Demographic analysis of NYS candidates, based on age.",
                        "Age Group",
                        "Candidates per age group",
                        "Percentage per age group"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        ageData,
                        "Age Group",
                        reportTypeEnum.DOUGHNUT
                      )
                    }
                  />,
                ]
              }
            >
              {ageData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart
                  data={ageData.filter(item => item.value > 0)} color="ageDougnut" type ="Age Group" />
              )}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title="Race"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of NYS candidates, based on race.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                raceData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "raceDoughnut",
                        raceData,
                        reportTypeEnum.DOUGHNUT,
                        "Demographic analysis of NYS candidates, based on race.",
                        "Race"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        raceData,
                        "Race",
                        reportTypeEnum.DOUGHNUT
                      )
                    }
                  />,
                ]
              }
            >
              {raceData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart data={raceData.filter(item => item.value > 0)} color="raceDoughnut" type ="Race"/>
              )}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title="Gender"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of NYS candidates, based on gender.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                genderData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "csGender",
                        genderData,
                        reportTypeEnum.DOUGHNUT,
                        "Demographic analysis of NYS candidates, based on gender.",
                        "Gender"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        genderData,
                        "Gender",
                        reportTypeEnum.DOUGHNUT
                      )
                    }
                  />,
                ]
              }
            >
              {genderData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart data={genderData.filter(item => item.value > 0)} color="csGender" type ="Gender"/>
              )}
            </Card>
          </Col>
        </Row>
        <Row style={{ paddingBottom: 10 }} gutter={16}>
        <Col xs={24} md={12}>
            <Card
              title="Disability"
              bordered={false}              
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of NYS candidates, based on disability.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                disabilityData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "disabilityDoughnut",
                        disabilityData,
                        reportTypeEnum.DOUGHNUT,
                        "Demographic analysis of NYS candidates, based on disability.",
                        "Disability"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        disabilityData,
                        "Disability",
                        reportTypeEnum.DOUGHNUT
                      )
                    }
                  />,
                ]
              }
            >
              {disabilityData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart data={disabilityData.filter(item => item.value > 0)} color="disabilityDoughnut" type ="Disability"/>
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              title="Highest Qualification"
              bordered={false}
              style={{ height: "auto" }}
              extra={
                <Tooltip title="Demographic analysis of NYS candidates, based on highest qualification.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                qualificationData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "qualificationDoughnut",
                        qualificationData,
                        reportTypeEnum.DOUGHNUT,
                        "Highest Qualification.",
                        "Demographic analysis of NYS candidates, based on highest qualification"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        qualificationData,
                        "Demographic analysis of NYS candidates, based on highest qualification",
                        reportTypeEnum.DOUGHNUT
                      )
                    }
                  />,
                ]
              }
            >
              {qualificationData.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart data={qualificationData.filter(item => item.value > 0)} color="qualificationDoughnut" type ="Qualification"/>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
  function Tab2() {
    return (
      <div>
        <div className="report-content map nys-map-half">
          <Row span={25} style={{ paddingBottom: 40 }} gutter={24}>
            <Col xs={24} md={14}>
              <Card
                title="NYS Candidate Origin"
                bordered={false}
                className="nysOrigin"
                extra={
                  <Tooltip title="Where the NYS Candidates are from">
                    <InfoCircleTwoTone />
                  </Tooltip>
                }
                actions={
                  candidateOrigin.length > 0 && [
                    <ReportButtons
                      onPrint={() =>
                        handleGeoPrint(
                          "Where the NYS Candidates are from",
                          "NYS Origin of Candidates",
                          candidateOrigin,
                          reportTypeEnum.GEO_PROVINCES,
                          "NYS Candidates"
                        )
                      }
                      onDownload={() =>
                        handleDataDownload(
                          candidateOrigin,
                          "NYS Candidate Origin",
                          reportTypeEnum.GEO_PROVINCES
                        )
                      }
                    />,
                  ]
                }
              >
                <ProvinceEventsMap
                  data={candidateOrigin.filter(item => item.value > 0)}
                  hoverText={"NYS Candidate Origin"}
                  dataSource="NYS Candidates"
                />
                <Legend />
              </Card>
            </Col>
            <Col xs={24} md={10}>
            <Card
              title="NYS Candidate Placement(Province)"
              bordered={false}
              extra={
                <Tooltip title="Distribution of the provinces where candidates are placed, compared to their preferred placement provinces.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                placementLocation.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "activities",
                        placementLocation,
                        reportTypeEnum.GROUPBAR,
                        "NYS Candidate Placement(Province).",
                        "Distribution of the provinces where candidates are placed, compared to their preferred placement provinces",
                        "",
                        "Percentage per Province",
                        "type",
                        "category",
                        "value"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        placementLocation,
                        "Distribution of the provinces where candidates are placed, compared to their preferred placement provinces",
                        reportTypeEnum.GROUPBAR
                      )
                    }
                  />,
                ]
              }
            >
              {placementLocation.length === 0 ? (
                <Empty />
              ) : (
                <GroupColumnChart
                  data={placementLocation.filter(item => item.value > 0)}
                  color="activities"
                  xField="type"
                  yField="value"
                  colorField="category"
                  yAxisLabel="Percentage per Province"
                />
              )}
            </Card>
          </Col>
          </Row>
        </div>
        <Row style={{ paddingBottom: 10 }} gutter={16}>
        
        </Row>
      </div>
    );
  }
  function Tab3() {
    return (
      <div>
        <Row style={{ paddingBottom: 10 }} gutter={16}>
          <Col xs={24} md={8}>
            <Card
              title="NYS Candidate Participation"
              bordered={false}
              extra={
                <Tooltip title="Total number of NYS candidates that have been placed at organisations or institutions.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                participationData.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "participation",
                        participationData,
                        reportTypeEnum.GROUPBAR,
                        "Demographic analysis of Olympiads, based on disability.",
                        "Participants Disability Distribution",
                        "",
                        "Percentage of placement per organization per year",
                        "year",
                        "type",
                        "value"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        participationData,
                        "Participants Disability Distribution",
                        reportTypeEnum.GROUPBAR
                      )
                    }
                  />,
                ]
              }
            >
              {participationData.length === 0 ? (
                <Empty />
              ) : (
                <GroupColumnChart
                  data={participationData.filter(item => item.value > 0)}
                  color="participation"
                  xField="year"
                  yField="value"
                  colorField="type"
                  yAxisLabel="Percentage of placement per organization per year"
                />
              )}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title="Time in NYS Programme"
              bordered={false}
              extra={
                <Tooltip title="How long NYS Candidates stayed in the NYS Programme.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                placementDuration.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "demographicPie",
                        placementDuration,
                        reportTypeEnum.DOUGHNUT,
                        "How long NYS Candidates stayed in the NYS Programme.",
                        "Time in NYS Programme"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        placementDuration,
                        "Time in NYS Programme",
                        reportTypeEnum.DOUGHNUT
                      )
                    }
                  />,
                ]
              }
            >
              {placementDuration.length === 0 ? (
                <Empty />
              ) : (
                <DoughNutChart data={placementDuration.filter(item => item.value > 0)} color="ageDougnut" type ="Age"/>
              )}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title="Before/After Activities of DSTI-NYS Candidates"
              bordered={false}
              extra={
                <Tooltip title="Activities of candidates before and after the NYS programme.">
                  <InfoCircleTwoTone />
                </Tooltip>
              }
              actions={
                candidateActivities.length > 0 && [
                  <ReportButtons
                    onPrint={() =>
                      handlePrint(
                        "activities",
                        candidateActivities,
                        reportTypeEnum.GROUPBAR,
                        "Activities of candidates before and after the NYS programme.",
                        "Before/After Activities of DSTI-NYS Candidates",
                        "",
                        "Percentage per activity",
                        "type",
                        "category",
                        "value"
                      )
                    }
                    onDownload={() =>
                      handleDataDownload(
                        candidateActivities,
                        "Before/After Activities of DSTI-NYS Candidates",
                        reportTypeEnum.GROUPBAR
                      )
                    }
                  />,
                ]
              }
            >
              {candidateActivities.length === 0 ? (
                <Empty />
              ) : (
                <GroupColumnChart
                  color="activities"
                  data={candidateActivities.filter(item => item.value > 0)}
                  xField="type"
                  yField="value"
                  colorField="category"
                  yAxisLabel="Percentage per activity"
                />
              )}
            </Card>
          </Col>
        </Row>
       
        <Row style={{ paddingBottom: 10 }} gutter={16}>
        
        </Row>
      </div>
    );
  }
  const tabList = [
    {
      key: "Participants Demographic",
      label: "Participants Demographic",
      children: <Tab1 />,
    },
    {
      key: "Participants Geographic Location",
      label: "Participants Geographic Location",
      children: <Tab2 />,
    },
    {
      key: "Participant Activities",
      label: "Participant Activities",
      children: <Tab3 />,
    },
  ];
  const onTabChange = (key) => {
    switch (key) {
      case "Participants Demographic":
        getAgeReportData(reportYear);
        getRaceData(reportYear);
        getGenderData(reportYear);
        getDisabilityData(reportYear);
        getQualificationData(reportYear);
        break;
      case "Participants Geographic Location":
        getCandidateOrigin(reportYear);
        break;
      case "Participant Activities":
        getParticipation();
        getPlacementDuration(reportYear);
        getCandidateActivities(reportYear)
        break;

      default:
        break;
    }
  };

  return (
    <>
      <div className="report-nys-content">
      <h2 className="report-heading">National Youth Service Report</h2>
        <div className="reportYear">
        {/* <Card bordered={true} className="reportYear"
        title="National Youth Service Report"
        size="small"
        headStyle={{fontSize:'16px', fontWeight:'bold' }}> */}
          <div className="reportYearContainer">
            <div
              style={{
                width: 400,
                margin: "0px auto",
                textAlign: "center",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Financial year : &nbsp;
              {/* <DatePicker
                picker="year"
                onChange={handleChange}
                // defaultValue={reportYear ? dayjs(reportYear) : null} 
                defaultValue={dayjs((reportYear).toString(), monthFormat)}
                format={monthFormat}
                allowClear={false}
                disabledDate={disabledDate}
              /> */}
              <Select
                    // style={{ width: "65%" }}
                    placeholder="Please select financial year"
                    onChange={onYearChange}
                    loading={!financialYears.length}
                    value={reportYear}
                    size="large"
                  >
                    {financialYears?.map((year) => {
                      return (
                        <Option key={year.id} value={year.id}>
                          {year.value}
                        </Option>
                      );
                    })}
                  </Select>
            </div>
          </div>
        {/* </Card> */}
        </div>
      </div>
      <div className="space"></div>
      <Tabs
        type="card"
        className="event-information"
        items={tabList}
        key="1"
        onChange={onTabChange}
      />

      <ReportTemplate
        onCancel={handlePrintCancel}
        reportOutProps={reportOutProps}
        show={show}
        key="reportTemplate"
        isGeo={isGeo}
      />
    </>
  );
};